/**
 * Current Plan Card Component
 * Displays the user's current subscription plan status
 */

import {
  getBillingCycleDescriptor,
  getPackageMonthlyEquivalent,
  getPackageTotalPriceForCycle,
} from '@/renderer/services/billingPricing';
import {
  BillingRecord,
  SubscriptionPackageDetails,
} from '@/renderer/types/billing';
import {
  AlertCircle,
  Calendar,
  CreditCard,
  Layers3,
  RefreshCcw,
  Sparkles,
} from 'lucide-react';
import React from 'react';

import { BillingMetricPill } from './BillingMetricPill';
import { BillingStatusBadge } from './BillingStatusBadge';
import { BillingSurface } from './BillingSurface';
import {
  formatBillingDate,
  formatMoney,
  getSubscriptionLabel,
  getSubscriptionTone,
} from './formatters';

interface CurrentPlanCardProps {
  subscription: BillingRecord;
  currentPackage: SubscriptionPackageDetails | null;
  isTrialing: boolean;
  isCanceled: boolean;
  trialDaysRemaining: number;
  onManageBilling: () => void;
  onResumeSubscription: () => void;
  isLoading: boolean;
}

export const CurrentPlanCard: React.FC<CurrentPlanCardProps> = ({
  subscription,
  currentPackage,
  isTrialing,
  isCanceled,
  trialDaysRemaining,
  onManageBilling,
  onResumeSubscription,
  isLoading,
}) => {
  const featurePreview = currentPackage?.features.slice(0, 4) || [];
  const currentBillingCycle = subscription.billing_cycle; // || 'monthly';
  const monthlyEquivalent =
    currentPackage != null
      ? getPackageMonthlyEquivalent(currentPackage, currentBillingCycle)
      : null;
  const cycleTotal =
    currentPackage != null
      ? getPackageTotalPriceForCycle(currentPackage, currentBillingCycle)
      : null;
  const priceLabel = cycleTotal ? formatMoney(cycleTotal) : '';

  const cycleWindow =
    subscription.current_period_start && subscription.current_period_end
      ? `${formatBillingDate(subscription.current_period_start, {
          day: 'numeric',
          month: 'short',
        })} to ${formatBillingDate(subscription.current_period_end, {
          day: 'numeric',
          month: 'short',
        })}`
      : 'Pending confirmation';

  return (
    <BillingSurface
      tone="inverse"
      className="relative overflow-hidden border-emerald-500/10"
    >
      <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-emerald-500/20 to-transparent rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 h-48 w-48 rounded-full bg-gradient-to-tr from-emerald-300/10 to-transparent blur-3xl" />

      <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-2xl">
          <div className="flex flex-wrap items-center gap-2">
            <BillingStatusBadge
              label={getSubscriptionLabel(subscription)}
              tone={getSubscriptionTone(subscription)}
            />
            {isTrialing && (
              <BillingStatusBadge
                icon={<Sparkles size={12} />}
                label={`${trialDaysRemaining} days left in trial`}
                tone="warning"
              />
            )}
            {isCanceled && (
              <BillingStatusBadge
                icon={<AlertCircle size={12} />}
                label="Cancellation scheduled"
                tone="danger"
              />
            )}
          </div>

          <div className="mt-4">
            <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-emerald-200/80">
              Subscription overview
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-white sm:text-3xl">
              {currentPackage?.name || subscription.package}
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-6 text-slate-300">
              {currentPackage?.description ||
                'Manage renewals, plan changes, and billing details from one compact workspace.'}
            </p>
          </div>

          {featurePreview.length > 0 && (
            <div className="mt-5 grid grid-cols-1 gap-2 text-sm text-slate-200 sm:grid-cols-2">
              {featurePreview.map((feature) => (
                <div
                  key={feature}
                  className="flex items-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-3 py-2"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={onManageBilling}
              disabled={isLoading}
              className="inline-flex items-center justify-center rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-slate-900 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Manage Billing
            </button>
            {isCanceled && (
              <button
                type="button"
                onClick={onResumeSubscription}
                disabled={isLoading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-sm font-semibold text-emerald-100 transition hover:bg-emerald-400/15 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <RefreshCcw size={14} />
                Resume Plan
              </button>
            )}
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:w-[26rem]">
          <BillingMetricPill
            label={getBillingCycleDescriptor(currentBillingCycle)}
            value={priceLabel}
            hint={
              (currentBillingCycle === 'annual' ||
                currentBillingCycle === 'semi_anually') &&
              monthlyEquivalent != null
                ? `${formatMoney(monthlyEquivalent)}/month equivalent`
                : getBillingCycleDescriptor(currentBillingCycle)
            }
            icon={<CreditCard size={16} />}
            tone="inverse"
          />
          <BillingMetricPill
            label={isCanceled ? 'Access until' : 'Renews'}
            value={formatBillingDate(subscription.current_period_end)}
            hint={
              isCanceled
                ? 'Your access remains active until then.'
                : 'Automatic renewal is enabled.'
            }
            icon={<Calendar size={16} />}
            tone="inverse"
          />
          <BillingMetricPill
            label="Current period"
            value={cycleWindow}
            hint="Usage totals reset when the cycle rolls over."
            icon={<Layers3 size={16} />}
            tone="inverse"
          />
          <BillingMetricPill
            label={isTrialing ? 'Trial ends' : 'Plan status'}
            value={
              isTrialing
                ? formatBillingDate(subscription.trial_ends_at)
                : getSubscriptionLabel(subscription)
            }
            hint={
              isTrialing
                ? `${trialDaysRemaining} day${trialDaysRemaining === 1 ? '' : 's'} remaining`
                : 'Synced with your latest Stripe subscription state.'
            }
            icon={
              isTrialing ? <Sparkles size={16} /> : <AlertCircle size={16} />
            }
            tone="inverse"
          />
        </div>
      </div>
    </BillingSurface>
  );
};
